import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { dataService } from '../services/dataService';
import { Client, BannerSet } from '../types';
import { Plus, Settings, Trash2 } from 'lucide-react';

export const ClientDashboard: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) return;
      try {
        const data = await dataService.getClient(clientId);
        setClient(data);
      } catch (error) {
        showToast('Failed to load client', 'error');
        navigate('/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClient();
  }, [clientId, navigate, showToast]);

  const handleUpdateClient = async (updates: Partial<Client>) => {
    if (!client) return;
    try {
      const updatedClient = await dataService.updateClient(client.id, updates);
      setClient(updatedClient);
      showToast('Client updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update client', 'error');
    }
  };

  const handleCreateBannerSet = async () => {
    if (!client) return;
    try {
      const newBannerSet: BannerSet = {
        id: '',
        name: 'New Banner Set',
        description: 'Add a description',
        status: 'draft',
        sizes: [],
        dataFeed: '',
        dataFeedArray: [],
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };
      const createdBannerSet = await dataService.createBannerSet(client.id, newBannerSet);
      setClient(prev => prev ? {
        ...prev,
        bannerSets: [...prev.bannerSets, createdBannerSet]
      } : null);
      showToast('Banner set created successfully', 'success');
    } catch (error) {
      showToast('Failed to create banner set', 'error');
    }
  };

  const handleDeleteBannerSet = async (bannerSetId: string) => {
    if (!client) return;
    try {
      await dataService.deleteBannerSet(client.id, bannerSetId);
      setClient(prev => prev ? {
        ...prev,
        bannerSets: prev.bannerSets.filter(bs => bs.id !== bannerSetId)
      } : null);
      showToast('Banner set deleted successfully', 'success');
    } catch (error) {
      showToast('Failed to delete banner set', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
          <p className="text-gray-600 mt-1">{client.description}</p>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={() => handleUpdateClient({ isActive: !client.isActive })}
            className={`px-4 py-2 rounded-lg ${
              client.isActive
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            {client.isActive ? 'Active' : 'Inactive'}
          </button>
          <button
            onClick={() => navigate(`/clients/${client.id}/settings`)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Settings size={16} />
            <span>Settings</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {client.bannerSets.map(bannerSet => (
          <div
            key={bannerSet.id}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{bannerSet.name}</h2>
                <p className="text-gray-600">{bannerSet.description}</p>
              </div>
              <button
                onClick={() => handleDeleteBannerSet(bannerSet.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="flex justify-between items-center">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                bannerSet.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : bannerSet.status === 'draft'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {bannerSet.status}
              </span>
              <button
                onClick={() => navigate(`/clients/${client.id}/banner-sets/${bannerSet.id}`)}
                className="text-blue-600 hover:text-blue-700"
              >
                Edit
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={handleCreateBannerSet}
          className="flex items-center justify-center space-x-2 bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300"
        >
          <Plus size={24} className="text-gray-400" />
          <span className="text-gray-600">Add New Banner Set</span>
        </button>
      </div>
    </div>
  );
};